import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });

    const data = await response.json().catch(() => ({}));
    // Non-OK responses without an API statusCode must not look like success
    // (empty {} used to pass isCuratedArticlesApiSuccess via statusCode == null).
    if (!response.ok && (data == null || data.statusCode == null)) {
      return {
        statusCode: '50000',
        message: data?.message || `Request failed (${response.status})`,
        httpStatus: response.status,
      };
    }
    return { ...data, httpStatus: response.status };
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

const authMultipartFetch = async (path, formData, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = { ...(optionHeaders || {}) };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
      body: formData,
    });

    return response.json().catch(() => ({}));
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

export const isCuratedArticlesApiSuccess = (res) => {
  if (String(res?.statusCode || '') === '10000') return true;
  // Only treat missing statusCode as success for OK HTTP responses
  if (res?.statusCode == null && res?.httpStatus >= 200 && res?.httpStatus < 300) return true;
  return false;
};

export const getCuratedArticlesPayload = (res) => res?.data ?? null;

/** GET /admin/curated-articles/sources */
export const getCuratedArticleSources = () =>
  authFetch('/admin/curated-articles/sources', { method: 'GET' });

/** POST /admin/curated-articles/sources */
export const createCuratedArticleSource = (body) =>
  authFetch('/admin/curated-articles/sources', { method: 'POST', body: JSON.stringify(body) });

/** PUT /admin/curated-articles/sources/:id */
export const updateCuratedArticleSource = (id, body) =>
  authFetch(`/admin/curated-articles/sources/${id}`, { method: 'PUT', body: JSON.stringify(body) });

/** DELETE /admin/curated-articles/sources/:id */
export const deleteCuratedArticleSource = (id) =>
  authFetch(`/admin/curated-articles/sources/${id}`, { method: 'DELETE' });

/** GET /admin/curated-articles */
export const getCuratedArticles = ({ status } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const query = params.toString();
  return authFetch(`/admin/curated-articles${query ? `?${query}` : ''}`, { method: 'GET' });
};

/** GET /admin/curated-articles/:id */
export const getCuratedArticle = (id) =>
  authFetch(`/admin/curated-articles/${id}`, { method: 'GET' });

/** POST /admin/curated-articles */
export const createCuratedArticle = (body) =>
  authFetch('/admin/curated-articles', { method: 'POST', body: JSON.stringify(body) });

/** PATCH /admin/curated-articles/:id */
export const updateCuratedArticle = (id, body) =>
  authFetch(`/admin/curated-articles/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** DELETE /admin/curated-articles/:id — soft-delete → archived; drops from Pulse / list */
export const deleteCuratedArticle = (id) =>
  authFetch(`/admin/curated-articles/${id}`, { method: 'DELETE' });

/** @deprecated Prefer deleteCuratedArticle */
export const archiveCuratedArticle = deleteCuratedArticle;

/** POST /admin/curated-articles/upload-image */
export const uploadCuratedArticleImage = (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  return authMultipartFetch('/admin/curated-articles/upload-image', formData, { method: 'POST' });
};

/** POST /admin/curated-articles/:id/generate — body: sourceUrls?, slideCount, generationPrompt */
export const generateCuratedArticle = (id, body) =>
  authFetch(`/admin/curated-articles/${id}/generate`, {
    method: 'POST',
    ...(body && Object.keys(body).length > 0 ? { body: JSON.stringify(body) } : {}),
  });

/** POST /admin/curated-articles/:id/cover-crop — body: sourceImageUrl, focusY, coverTitle? */
export const cropCuratedArticleCover = (id, body) =>
  authFetch(`/admin/curated-articles/${id}/cover-crop`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** GET /admin/curated-articles/:id/preview */
export const getCuratedArticlePreview = (id) =>
  authFetch(`/admin/curated-articles/${id}/preview`, { method: 'GET' });

/** PATCH /admin/curated-articles/:id/briefs/:briefId */
export const updateCuratedArticleBrief = (id, briefId, body) =>
  authFetch(`/admin/curated-articles/${id}/briefs/${briefId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** DELETE /admin/curated-articles/:id/briefs/:briefId */
export const deleteCuratedArticleBrief = (id, briefId) =>
  authFetch(`/admin/curated-articles/${id}/briefs/${briefId}`, { method: 'DELETE' });

/** POST /admin/curated-articles/:id/publish */
export const publishCuratedArticle = (id) =>
  authFetch(`/admin/curated-articles/${id}/publish`, { method: 'POST' });

/** POST /admin/curated-articles/:id/unpublish */
export const unpublishCuratedArticle = (id) =>
  authFetch(`/admin/curated-articles/${id}/unpublish`, { method: 'POST' });
